import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MobileMenuContext } from './Footer';
import youCrmLogo from '../assets/youcrm.svg';

const Navbar: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Reset scroll position when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'hu' : 'en');
  };

  // Helper function to determine if a route is active
  const isActive = (path: string) => location.pathname === path;

  // Handle menu item click to close mobile menu
  const handleMenuItemClick = () => {
    setIsMenuOpen(false);
  };

  // Context value for mobile menu
  const mobileMenuContextValue = {
    closeMobileMenu: handleMenuItemClick
  };

  return (
    <MobileMenuContext.Provider value={mobileMenuContextValue}>
      <nav className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="text-2xl font-bold" onClick={handleMenuItemClick}>
                  <img src={youCrmLogo} alt="YouCRM Logo" className="h-10" />
                </Link>
              </div>
              <div className="hidden sm:ml-8 sm:flex sm:space-x-8">
                <Link 
                  to="/" 
                  onClick={handleMenuItemClick}
                  className={`inline-flex items-center px-3 pt-1 border-b-2 text-md font-bold leading-5
                  ${isActive('/') 
                    ? 'border-indigo-500 text-indigo-600' 
                    : 'border-transparent text-gray-600 hover:text-indigo-600 hover:border-indigo-300'}`}
                >
                  {t('nav.home')}
                </Link>
                <Link 
                  to="/features" 
                  onClick={handleMenuItemClick}
                  className={`inline-flex items-center px-3 pt-1 border-b-2 text-md font-bold leading-5
                  ${isActive('/features') 
                    ? 'border-indigo-500 text-indigo-600' 
                    : 'border-transparent text-gray-600 hover:text-indigo-600 hover:border-indigo-300'}`}
                >
                  {t('nav.features')}
                </Link>
                <Link 
                  to="/pricing" 
                  onClick={handleMenuItemClick}
                  className={`inline-flex items-center px-3 pt-1 border-b-2 text-md font-bold leading-5
                  ${isActive('/pricing') 
                    ? 'border-indigo-500 text-indigo-600' 
                    : 'border-transparent text-gray-600 hover:text-indigo-600 hover:border-indigo-300'}`}
                >
                  {t('nav.pricing')}
                </Link>
                <Link 
                  to="/contact" 
                  onClick={handleMenuItemClick}
                  className={`inline-flex items-center px-3 pt-1 border-b-2 text-md font-bold leading-5
                  ${isActive('/contact') 
                    ? 'border-indigo-500 text-indigo-600' 
                    : 'border-transparent text-gray-600 hover:text-indigo-600 hover:border-indigo-300'}`}
                >
                  {t('nav.contact')}
                </Link>
              </div>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:items-center">
              <button
                onClick={toggleLanguage}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {language === 'en' ? 'HU' : 'EN'}
              </button>
              <div className="ml-4">
                <Link to="/contact" onClick={handleMenuItemClick} className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-md text-sm font-medium text-white hover:from-indigo-700 hover:to-purple-700 shadow-md hover:shadow-lg transition-all duration-300">
                  {t('hero.cta')}
                </Link>
              </div>
            </div>
            <div className="-mr-2 flex items-center sm:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <span className="sr-only">Open main menu</span>
                {/* Icon for menu */}
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="sm:hidden">
            <div className="pt-2 pb-3 space-y-1">
              <Link 
                to="/" 
                onClick={handleMenuItemClick}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium 
                ${isActive('/') 
                  ? 'border-indigo-500 text-indigo-700 bg-indigo-50' 
                  : 'border-transparent text-gray-600 hover:text-indigo-700 hover:bg-gray-50 hover:border-indigo-300'}`}
              >
                {t('nav.home')}
              </Link>
              <Link 
                to="/features" 
                onClick={handleMenuItemClick}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium 
                ${isActive('/features') 
                  ? 'border-indigo-500 text-indigo-700 bg-indigo-50' 
                  : 'border-transparent text-gray-600 hover:text-indigo-700 hover:bg-gray-50 hover:border-indigo-300'}`}
              >
                {t('nav.features')}
              </Link>
              <Link 
                to="/pricing" 
                onClick={handleMenuItemClick}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium 
                ${isActive('/pricing') 
                  ? 'border-indigo-500 text-indigo-700 bg-indigo-50' 
                  : 'border-transparent text-gray-600 hover:text-indigo-700 hover:bg-gray-50 hover:border-indigo-300'}`}
              >
                {t('nav.pricing')}
              </Link>
              <Link 
                to="/contact" 
                onClick={handleMenuItemClick}
                className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium 
                ${isActive('/contact') 
                  ? 'border-indigo-500 text-indigo-700 bg-indigo-50' 
                  : 'border-transparent text-gray-600 hover:text-indigo-700 hover:bg-gray-50 hover:border-indigo-300'}`}
              >
                {t('nav.contact')}
              </Link>
            </div>
            <div className="pt-4 pb-3 border-t border-gray-200">
              <div className="flex items-center justify-between px-4">
                <button
                  onClick={toggleLanguage}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {language === 'en' ? 'HU' : 'EN'}
                </button>
                <div>
                  <Link to="/contact" onClick={handleMenuItemClick} className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-md text-sm font-medium text-white hover:from-indigo-700 hover:to-purple-700 shadow-md">
                    {t('hero.cta')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </MobileMenuContext.Provider>
  );
};

export default Navbar; 